/**
 * An anchored nav-bar popover: a panel with a caret pointing back at its
 * trigger, plus the dismissal behaviour every such menu needs.
 *
 * WHY THIS EXISTS. The app has three independent copy-pastes of this idea —
 * `NotificationsMenu` (Redux visibility + a JS-measured caret), `MenuUser`
 * (Redux visibility + a static caret), and `CirclesDrawer/usePortalMenu`
 * (portalled, fixed-positioned, no caret) — and only the last handles Escape.
 * Rather than write a fourth, the shape is factored here. It is scoped to
 * `Circles/shared/` deliberately: the two header menus render on every page for
 * every logged-in user, so migrating them is a separate change with its own
 * testing. Promoting this to `lib/components/NavPopover/` when that happens is
 * a file move.
 *
 * NOT PORTALLED, on purpose. Rendering in-tree keeps the panel inside
 * `#header-app`'s stacking context, which is what lets a plain `z-index` work
 * and keeps it out of the body-portal z-index inventory documented in
 * `_CirclesNavEntry.scss` (while the Circles drawer is open `#header-app` rises
 * to `$overlay-z-index + 11`, and anything portalled below that paints behind
 * the very drawer that opened it). The caller must give the anchor
 * `position: relative`.
 */
import React, { useCallback, useEffect, useRef } from 'react'

/**
 * Why the close callback carries a reason: the dialog contract restores focus
 * to the trigger on a KEYBOARD close (Escape), but never on an outside
 * pointer-click — the click already moved focus where the user pointed, and
 * yanking it back would fight mouse users. The caller owns the trigger, so it
 * owns the restore; this component only reports which path closed it.
 */
export type NavPopoverCloseReason = 'escape' | 'outside'

export interface NavPopoverProps {
  isOpen: boolean
  /** Called for outside-click and Escape. The caller owns the open state. */
  onClose: (reason: NavPopoverCloseReason) => void
  /** Panel width in px at desktop widths. Below the mobile breakpoint it goes edge-to-edge. */
  width?: number
  /**
   * Distance in px from the panel's RIGHT edge to the caret's centre. Static
   * rather than measured: unlike the notifications bell — whose panel spans the
   * header, forcing a runtime measurement — a nav trigger of known width sitting
   * in its own `position: relative` wrapper resolves to a constant.
   */
  caretOffset?: number
  /** Distance in px the panel's right edge sits OUTSIDE the anchor's right edge. */
  rightOffset?: number
  id?: string
  className?: string
  labelledBy?: string
  children: React.ReactNode
}

const NavPopoverBase: React.FC<NavPopoverProps> = ({
  isOpen,
  onClose,
  width = 383,
  caretOffset = 43,
  rightOffset = 23,
  id,
  className = '',
  labelledBy,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  // Held in a ref so the two document listeners below can depend on `isOpen`
  // alone and never re-bind when the caller passes a fresh `onClose` identity.
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  const handlePointerDown = useCallback((event: MouseEvent) => {
    const panel = panelRef.current
    if (!panel) return
    const target = event.target as Node | null
    // The anchor (and therefore the trigger) is the panel's parent: a click on
    // the trigger must fall through to its own toggle handler rather than being
    // treated as an outside-click, or the two would fight and the menu would
    // close-then-reopen on every trigger press.
    const anchor = panel.parentElement
    if (target && anchor && anchor.contains(target)) return
    onCloseRef.current('outside')
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    // `pointerdown`, not `click`: matches usePortalMenu, and closes before a
    // click on the page underneath has a chance to act on a stale layout.
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isOpen, handlePointerDown])

  // Dialog focus contract, half 1: `role="dialog"` promises focus moves INTO
  // the dialog on open (ARIA APG) — without this a screen reader hears "dialog"
  // and then nothing happens until a blind Tab forward. The panel div carries
  // tabIndex={-1} so it can take programmatic focus without joining the Tab
  // order. Half 2 (restore on Escape) lives with the caller via the close
  // reason above. Same pattern as usePortalMenu, which this file cites.
  useEffect(() => {
    if (isOpen) panelRef.current?.focus()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onCloseRef.current('escape')
    }
    // CAPTURE phase, bound at `document`. React 18 delegates `keydown` at its
    // root container, which a bubbling event reaches BEFORE `document` — so a
    // bubble-phase `stopPropagation` here would fire too late to stop a host
    // component's own Escape handling. Same reasoning as usePortalMenu.
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      ref={panelRef}
      id={id}
      className={`nav-popover ${className}`.trim()}
      role="dialog"
      aria-modal={false}
      tabIndex={-1}
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
      style={{
        '--nav-popover-width': `${width}px`,
        '--nav-popover-caret-offset': `${caretOffset}px`,
        '--nav-popover-right-offset': `${-rightOffset}px`,
      } as React.CSSProperties}
    >
      <span className="nav-popover__caret" aria-hidden="true" />
      <div className="nav-popover__panel">{children}</div>
    </div>
  )
}

export const NavPopover = React.memo(NavPopoverBase)

export default NavPopover
